import { getChannelClient } from 'src/utils/messages/singleton'

// Rounding mode: `data-round="floor"` on the script tag asks for
// Math.floor (impact widget needs this to avoid an off-by-a-pixel
// scrollbar that the old hand-rolled `body.offsetHeight - 1` was
// avoiding); default is Math.ceil, which suits most callers.
const round = document.currentScript?.dataset.round === 'floor'
  ? Math.floor
  : Math.ceil

const docElement = document.documentElement

const getDocumentHeight = () =>
  round(docElement.getBoundingClientRect().height)

// Observe documentElement and route resize fires through the given
// callback. Lock overflow on the html element so the iframe's host
// owns the visible size; returns a teardown that restores the
// previous overflow and stops the observer (used in the cross-origin
// failure path so an improperly-embedded widget falls back to its
// natural scrolling instead of being stuck with hidden overflow plus
// a stale fixed height).
const observe = resize => {
  const previousOverflow = docElement.style.overflow
  docElement.style.overflow = 'hidden'
  const observer = new ResizeObserver(() => {
    const height = getDocumentHeight()
    if (height > 0) resize(height)
  })
  observer.observe(docElement)
  return () => {
    observer.disconnect()
    docElement.style.overflow = previousOverflow
  }
}

// Same-origin: set the parent iframe element's height directly via
// frameElement — no messaging needed. Cross-origin: send through the
// shared channel client so other bundles on this page (the donate
// widget React app, future modal client, etc.) share one handshake;
// if the handshake never completes (parent didn't load our embed
// script), tear down so the iframe can scroll naturally.
//
// Both paths gate on a positive height — a ResizeObserver fire with
// height 0 (possible briefly between parse and first paint, especially
// when the include lands in <head>) would otherwise collapse the
// hosting iframe to invisible before the next fire restores it.
if (frameElement) {
  observe(height => { frameElement.style.height = height + 'px' })
} else if (self != parent) {
  const client = getChannelClient()
  const stop = observe(height => { client.send('setWidgetHeight', { height }) })
  client.ready.catch(stop)
}
